import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
	Box,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
} from "@mui/material";
import { CalendarIcon, DatePicker } from "@mui/x-date-pickers";
import { get } from "lodash-es";
import moment from "moment-timezone";
import React, { ReactNode, useEffect, useState } from "react";
import { SearchShiftsParameters } from "../models/SearchShiftsParameters";
import { ShiftData } from "../models/shift-data";
import { getShiftsRangeByParameters } from "../repositories/ShiftsRepository";

interface PeriodOption {
	value: string;
	label: string;
}

type Props = {
	value: SearchShiftsParameters | null;
	actualPeriod?: string | null;
	onChange: (value: any, period: string | null) => void;
	showShiftAndCrews?: boolean;
	dateFormat?: string;
	periodOptions?: PeriodOption[];
};

const DEFAULT_PERIOD_OPTIONS: PeriodOption[] = [
	{ value: "Shift", label: "Shift" },
	{ value: "Day", label: "Day" },
	{ value: "4Days", label: "4 Days" },
	{ value: "Week", label: "Week" },
	{ value: "Month", label: "Month" },
];

export const ShiftPeriodNavigatorControl = ({
	value,
	actualPeriod,
	onChange,
	showShiftAndCrews = true,
	dateFormat = "MM/DD/YYYY HH:mm a",
	periodOptions = DEFAULT_PERIOD_OPTIONS,
}: Props) => {
	const [period, setPeriod] = useState(
		actualPeriod
			? (periodOptions.find((o) => o.value == actualPeriod)?.value ?? null)
			: periodOptions[0]?.value || "Day",
	);
	const [loadingShiftPeriodList, setLoadingShiftPeriodList] = useState(false);
	const [error, setError] = useState("");
	const [shiftPeriodList, setShiftPeriodList] = useState<ShiftData[]>([]);
	const [isActionInProgress, setIsActionInProgress] = useState(false);
	const [customStartDate, setCustomStartDate] = useState<moment.Moment | null>(
		null,
	);
	const [customEndDate, setCustomEndDate] = useState<moment.Moment | null>(
		null,
	);

	const handleArrowNavigation = async (direction: "next" | "prev") => {
		if (period === "Custom") return;
		if (!startShift?.Start && !endShift?.End) return;

		try {
			if (direction === "prev" && startShift?.Start) {
				await searchShiftsRangeByParameters(
					period,
					moment(startShift.Start).toDate(),
					null,
					false,
					"prev",
				);
			} else if (direction === "next" && endShift?.End) {
				await searchShiftsRangeByParameters(
					period,
					null,
					moment(endShift.End).toDate(),
					false,
					"next",
				);
			}
		} catch (error) {
			setError(error as string);
		}
	};

	const handleLastShift = async () => {
		if (period === "Custom") return;
		try {
			await searchShiftsRangeByParameters(period, null, null, false, "last");
		} catch (error) {
			setError(error as string);
		}
	};

	const searchShiftsRangeByParameters = async (
		newPeriod: string | null,
		newStartDate: Date | null,
		newEndDate: Date | null,
		useShiftIds: boolean | null = true,
		action: "prev" | "next" | "last" | null = null,
	) => {
		if (isActionInProgress) return;
		setIsActionInProgress(true);
		setLoadingShiftPeriodList(true);
		try {
			const res = await getShiftsRangeByParameters(
				newPeriod,
				newStartDate,
				newEndDate,
				useShiftIds ? (value?.StartShiftId ?? null) : null,
				useShiftIds ? (value?.EndShiftId ?? null) : null,
				action,
			);

			if (res.ok) {
				const rowsSource = get(res, "data.tables[0].rows", []);
				setShiftPeriodList(rowsSource);

				if (newPeriod === "Custom") {
					if (rowsSource.length >= 1) {
						const firstShift = rowsSource[0];
						const lastShift = rowsSource[rowsSource.length - 1];
						onChange(
							{
								StartShiftId: firstShift.ShiftId,
								EndShiftId: lastShift.ShiftId,
							},
							newPeriod,
						);
					}
				} else {
					if (rowsSource.length >= 2) {
						onChange(
							{
								StartShiftId: rowsSource[0].ShiftId,
								EndShiftId: rowsSource[1].ShiftId,
							},
							newPeriod,
						);
					} else if (rowsSource.length === 1) {
						onChange(
							{
								StartShiftId: rowsSource[0].ShiftId,
								EndShiftId: rowsSource[0].ShiftId,
							},
							newPeriod,
						);
					}
				}
			} else {
				setError(res.message as string);
			}
		} catch (error) {
			setError(error as string);
		} finally {
			setLoadingShiftPeriodList(false);
			setIsActionInProgress(false);
		}
	};

	const handleDateChange = async (
		value: moment.Moment | null,
		key: "start" | "end",
	) => {
		if (!value || !value.isValid()) return;

		if (period === "Custom") {
			if (key === "start") {
				setCustomStartDate(value);
				if (customEndDate) {
					await searchShiftsRangeByParameters(
						"Custom",
						value.toDate(),
						customEndDate.toDate(),
						false,
					);
				}
			} else {
				setCustomEndDate(value);
				if (customStartDate) {
					await searchShiftsRangeByParameters(
						"Custom",
						customStartDate.toDate(),
						value.toDate(),
						false,
					);
				}
			}
			return;
		}

		try {
			const newDate = value.toDate();

			if (key === "start") {
				await searchShiftsRangeByParameters(period, newDate, null, false);
			} else {
				await searchShiftsRangeByParameters(period, null, newDate, false);
			}
		} catch (error) {
			setError(error as string);
		}
	};

	const handleChangeScope = async (
		event: SelectChangeEvent<string | null>,
		child: ReactNode,
	) => {
		const newPeriod = event.target.value as string;
		setPeriod(newPeriod);

		if (newPeriod === "All") {
			setCustomStartDate(null);
			setCustomEndDate(null);
			setShiftPeriodList([]);
			onChange({ StartShiftId: null, EndShiftId: null }, "All");
			return;
		}

		if (newPeriod === "Custom") {
			const today = moment();
			const yesterday = moment().subtract(1, "day");
			setCustomStartDate(yesterday);
			setCustomEndDate(today);
			await searchShiftsRangeByParameters(
				"Custom",
				yesterday.toDate(),
				today.toDate(),
				false,
			);
		} else {
			setCustomStartDate(null);
			setCustomEndDate(null);
			await searchShiftsRangeByParameters(
				newPeriod,
				null,
				endShift?.Start ? moment(endShift.Start).toDate() : null,
				false,
			);
		}
	};

	useEffect(() => {
		if (period === "All") return;
		if (value?.StartShiftId || value?.EndShiftId) {
			searchShiftsRangeByParameters(period, null, null, true);
		}
	}, [value?.StartShiftId, value?.EndShiftId]);

	useEffect(() => {
		if (period === "All") return;
		searchShiftsRangeByParameters(period, null, null, true);
	}, []);

	const startShift = shiftPeriodList[0];
	const endShift = shiftPeriodList[1] || shiftPeriodList[0];

	const renderShiftCrewInfo = (shift: ShiftData | undefined) => {
		if (!showShiftAndCrews || !shift || period === "Custom") return null;

		return (
			<span
				style={{
					paddingRight: 5,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					fontSize: 15,
					fontWeight: "bold",
				}}
			>
				{shift.Shift} - {shift.Crew}
			</span>
		);
	};

	const isCustomMode = period === "Custom";
	const isAllMode = period === "All";

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns:
					"auto minmax(100px, 1fr) 80px minmax(100px, 1fr) auto auto",
				alignItems: "center",
				width: "100%",
				margin: 0,
				padding: "8px 0",
				gap: "8px",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
				}}
			>
				<IconButton
					size="small"
					color="primary"
					disabled={loadingShiftPeriodList || isCustomMode || isAllMode}
					onClick={() => handleArrowNavigation("prev")}
				>
					<ChevronLeft />
				</IconButton>
			</div>

			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					minWidth: "100px",
					fontSize: ".8rem!important",
					margin: "0!important",
					"& .MuiInputAdornment-root": {
						margin: "0!important",
					},
					"& .MuiInputBase-input": {
						fontSize: "1rem!important",
						padding: "4px 8px !important",
					},
				}}
			>
				<DatePicker
					label="Start"
					format={dateFormat}
					value={
						isAllMode
							? null
							: isCustomMode
								? customStartDate || moment().subtract(1, "day")
								: moment(startShift?.Start || moment().subtract(1, "day"))
					}
					onChange={(value) =>
						handleDateChange(value ? moment(value) : null, "start")
					}
					disabled={loadingShiftPeriodList || isAllMode}
					maxDate={isCustomMode ? moment() : undefined}
					enableAccessibleFieldDOMStructure={false}
					slots={{
						textField: (props) => (
							<TextField
								{...props}
								size="small"
								variant="outlined"
								fullWidth
								style={{ width: "100%" }}
								InputProps={{
									...props.InputProps,
									style: { paddingTop: "2px" },
								}}
							/>
						),
						openPickerIcon: () => (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									width: "100%",
								}}
							>
								{renderShiftCrewInfo(startShift)}
								<IconButton style={{ padding: "0px", flexShrink: 0 }}>
									<CalendarIcon />
								</IconButton>
							</div>
						),
					}}
				/>
			</Box>

			<Box
				sx={{
					width: "80px",
					display: "flex",
					alignItems: "center",
					"& .MuiInputBase-input": {
						fontSize: "0.8rem !important",
						padding: "4px 8px !important",
						display: "inline-flex",
						alignItems: "center",
					},
				}}
			>
				<FormControl variant="outlined" size="small" fullWidth>
					<InputLabel id="period-select-label">Period</InputLabel>
					<Select
						value={period}
						onChange={handleChangeScope}
						disabled={loadingShiftPeriodList}
						label="Period"
						labelId="period-select-label"
						style={{
							width: "100%",
						}}
					>
						{periodOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					minWidth: "100px",
					fontSize: ".8rem!important",
					margin: "0!important",
					"& .MuiInputAdornment-root": {
						margin: "0!important",
					},
					"& .MuiInputBase-input": {
						fontSize: "1rem!important",
						padding: "4px 8px !important",
					},
				}}
			>
				<DatePicker
					label="End"
					format={dateFormat}
					value={
						isAllMode
							? null
							: isCustomMode
								? customEndDate || moment()
								: moment(endShift?.End || endShift?.Start || moment())
					}
					onChange={(value) =>
						handleDateChange(value ? moment(value) : null, "end")
					}
					disabled={loadingShiftPeriodList || isAllMode}
					minDate={
						isCustomMode && customStartDate ? customStartDate : undefined
					}
					maxDate={isCustomMode ? moment() : undefined}
					enableAccessibleFieldDOMStructure={false}
					slots={{
						textField: (props) => (
							<TextField
								{...props}
								size="small"
								variant="outlined"
								fullWidth
								style={{ width: "100%" }}
								InputProps={{
									...props.InputProps,
									style: { paddingTop: "2px" },
								}}
							/>
						),
						openPickerIcon: () => (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									margin: "0 !important",
									width: "100%",
								}}
							>
								{renderShiftCrewInfo(endShift)}
								<IconButton style={{ padding: "0px", flexShrink: 0 }}>
									<CalendarIcon />
								</IconButton>
							</div>
						),
					}}
				/>
			</Box>

			<div
				style={{
					display: "flex",
					alignItems: "center",
				}}
			>
				<IconButton
					size="small"
					disabled={loadingShiftPeriodList || isCustomMode || isAllMode}
					onClick={() => handleArrowNavigation("next")}
				>
					<ChevronRight />
				</IconButton>
			</div>

			<div
				style={{
					display: "flex",
					alignItems: "center",
				}}
			>
				<IconButton
					size="small"
					disabled={loadingShiftPeriodList || isCustomMode || isAllMode}
					onClick={handleLastShift}
				>
					<SkipNextIcon />
				</IconButton>
			</div>
		</Box>
	);
};
